import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, CssBaseline } from '@mui/material';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter } from 'react-router-dom';
import { alpacaTheme } from './theme/theme';
import { NotificationProvider } from './components/notifications';
import { ConfirmProvider } from './components/confirm';
import { GlobalLoadingOverlay } from './components/loading';
import { App } from './App';

// App-wide typeface is Segoe UI first (BO UI Standard §1 V3, 2026-07-06) — the
// legacy ALPACA LIMS / DevExpress XAF face, present on Windows as a system font.
// No web-font bundle needed; the stack lives in --ai-font (theme/tokens.css) and
// the MUI theme. (Removed the @fontsource/public-sans imports — no longer used.)

// PUBLIC design tokens — the ONE source of truth for every --ai-* colour /
// surface / semantic token (BO UI Standard, docs/prototypes/).  Imported FIRST
// so every later stylesheet can reference the tokens.
import './theme/tokens.css';

// BO UI Standard PUBLIC shared styles — the one stylesheet for the standard
// form chrome (.ca-btn buttons, .tb-i glyphs, .section-title form title,
// .list-search grid search).  Feature files must never re-declare these.
import './theme/boStandard.css';

// Global loading-indicator styling (symbols, top bar, scrim) — §18.6.
import './components/loading/loading.css';

// Global icon-color palette — applies semantic colours to every .ca-btn
// icon across the whole app (Save = blue, Refresh = sky, PDF = adobe-red,
// etc.).  Single source of truth — edit src/theme/iconColors.css to
// retune one icon and it propagates to every form.
import './theme/iconColors.css';
// All text black except status (user request 2026-06-13).
import './theme/blackText.css';

const queryClient = new QueryClient();

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ThemeProvider theme={alpacaTheme}>
      <CssBaseline />
      <QueryClientProvider client={queryClient}>
        <NotificationProvider>
          <ConfirmProvider>
            {/* basename = the deploy base path (Vite BASE_URL) so client-side
                routes live under a sub-application (e.g. /alpacaintelligence_sandbox)
                instead of escaping to the site root.  Root/prod build → '/'. */}
            <BrowserRouter basename={import.meta.env.BASE_URL.replace(/\/$/, '') || '/'}>
              <App />
              <GlobalLoadingOverlay />
            </BrowserRouter>
          </ConfirmProvider>
        </NotificationProvider>
      </QueryClientProvider>
    </ThemeProvider>
  </React.StrictMode>,
);
